package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MenuDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.isCtrlPressed
import androidx.compose.ui.input.pointer.isPrimaryPressed
import androidx.compose.ui.input.pointer.isSecondaryPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.CustomAccessibilityAction
import androidx.compose.ui.semantics.customActions
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import kotlin.math.roundToInt

/*
 * SH-98: right-click menus for the mouse and trackpad of laptops (Googlebook, Chromebooks, a
 * browser). They offer the same actions a tap leads to (the shift's sheet, the person's form), so
 * nothing is only reachable by right-clicking; screen readers get them as custom actions.
 */

/** One action of a context menu. */
@Immutable
data class ContextMenuItem(
    val label: String,
    val onClick: () -> Unit,
    val icon: ImageVector? = null,
    val enabled: Boolean = true,
    /** Red: removes something. */
    val destructive: Boolean = false
)

/** Test tags of [ContextMenuArea]. */
object ContextMenuTags {
    const val MENU = "context_menu"
    fun item(index: Int) = "context_menu_item_$index"
}

/** The navy of the app's menus (a step lighter than the dialogs). */
private val MenuNavy: Color get() = com.evoapps.evoshift.ui.AppAppearance.palette.base.container
private val DestructiveOnNavy = Color(0xFFFF8A80)

/**
 * [content] with a context menu: a right click (secondary button, or Control-click) opens [items] where the pointer
 * is. [items] is read when the menu opens; no items (or [enabled] false): no menu, and the right
 * click goes to [content] as usual. A left click and a tap are untouched.
 */
@Composable
fun ContextMenuArea(
    items: () -> List<ContextMenuItem>,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    content: @Composable BoxScope.() -> Unit
) {
    var openAt by remember { mutableStateOf<Offset?>(null) }
    var shown by remember { mutableStateOf<List<ContextMenuItem>>(emptyList()) }
    val currentItems by rememberUpdatedState(items)
    val area = if (!enabled) modifier else modifier
        .pointerInput(Unit) {
            awaitPointerEventScope {
                while (true) {
                    // Initial pass: before the clickable inside sees the press as a click.
                    val event = awaitPointerEvent(PointerEventPass.Initial)
                    // A right click, or Control-click as on a Mac (SH-97: the macOS app).
                    val secondary = event.buttons.isSecondaryPressed ||
                        (event.buttons.isPrimaryPressed && event.keyboardModifiers.isCtrlPressed)
                    // SH-169: a press something above already used (the roster grid's Ctrl+click
                    // selecting cells) opens no menu.
                    if (event.type == PointerEventType.Press && secondary && event.changes.none { it.isConsumed }) {
                        val menu = currentItems()
                        if (menu.isNotEmpty()) {
                            event.changes.forEach { it.consume() }
                            shown = menu
                            openAt = event.changes.firstOrNull()?.position ?: Offset.Zero
                        }
                    }
                }
            }
        }
        .semantics {
            // The same actions for screen readers and switch access (and UI tests).
            customActions = items().map { item -> CustomAccessibilityAction(item.label) { item.onClick(); true } }
        }
    Box(area) {
        content()
        val position = openAt
        if (position != null) {
            Box(
                Modifier
                    .align(Alignment.TopStart)
                    .offset { IntOffset(position.x.roundToInt(), position.y.roundToInt()) }
                    .size(1.dp)
            ) {
                DropdownMenu(
                    expanded = true,
                    onDismissRequest = { openAt = null },
                    containerColor = MenuNavy,
                    modifier = Modifier.testTag(ContextMenuTags.MENU)
                ) {
                    shown.forEachIndexed { index, item ->
                        val color = if (item.destructive) DestructiveOnNavy else Color.White
                        DropdownMenuItem(
                            text = { Text(item.label) },
                            leadingIcon = item.icon?.let { icon -> { Icon(icon, contentDescription = null) } },
                            enabled = item.enabled,
                            onClick = {
                                openAt = null
                                item.onClick()
                            },
                            colors = MenuDefaults.itemColors(
                                textColor = color,
                                leadingIconColor = if (item.destructive) DestructiveOnNavy else LightGold,
                                disabledTextColor = Color.White.copy(alpha = 0.4f),
                                disabledLeadingIconColor = Color.White.copy(alpha = 0.3f)
                            ),
                            modifier = Modifier.handCursor().testTag(ContextMenuTags.item(index))
                        )
                    }
                }
            }
        }
    }
}
